/**
 * Pagination Atom — supports simple (page/totalPages) and offset/limit modes.
 * @layer atom
 * @package @plerity/ui-core
 */

import { cn } from "../utils/cn";
import type { PaginationProps } from "./Pagination.types";

// ── helpers ──────────────────────────────────────────────────────────────────

/** Build the page-button sequence with ellipsis for large page counts. */
function buildPageSequence(current: number, total: number): (number | "…")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | "…")[] = [1];
  if (current > 3) pages.push("…");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let p = start; p <= end; p++) pages.push(p);
  if (current < total - 2) pages.push("…");
  pages.push(total);
  return pages;
}

// ── shared sub-components ────────────────────────────────────────────────────

const NAV_BTN =
  "inline-flex items-center justify-center rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors " +
  "border border-gray-300 dark:border-gray-600 " +
  "text-gray-700 dark:text-gray-300 " +
  "bg-white dark:bg-gray-800 " +
  "hover:bg-gray-50 dark:hover:bg-gray-700 " +
  "disabled:opacity-40 disabled:cursor-not-allowed " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";

const PAGE_BTN_BASE =
  "inline-flex items-center justify-center rounded-md min-w-[2rem] px-2 py-1.5 text-sm font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";

const PAGE_BTN_INACTIVE = `${PAGE_BTN_BASE} text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700`;

const PAGE_BTN_ACTIVE = `${PAGE_BTN_BASE} bg-blue-600 text-white cursor-default`;

function PageButtons({ current, total, onGo }: { current: number; total: number; onGo: (page: number) => void }) {
  const seq = buildPageSequence(current, total);
  return (
    <div className="flex items-center gap-0.5">
      {seq.map((item, idx) =>
        item === "…" ? (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: up to 2 ellipsis entries can appear in one sequence (before/after the current-page window); idx disambiguates them. The sequence is recomputed deterministically from current/total each render, never reordered.
            key={`ellipsis-${idx}`}
            className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1.5 text-sm text-gray-400 dark:text-gray-500 select-none"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onGo(item)}
            disabled={item === current}
            aria-label={`Page ${item}`}
            aria-current={item === current ? "page" : undefined}
            className={item === current ? PAGE_BTN_ACTIVE : PAGE_BTN_INACTIVE}
          >
            {item}
          </button>
        )
      )}
    </div>
  );
}

// ── Prev/Next SVG arrows (no lucide-react dep) ────────────────────────────────

function ChevronLeft() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export function Pagination(props: PaginationProps) {
  const showPageNumbers = props.showPageNumbers ?? true;

  if (props.mode === "offset") {
    const { offset, limit, total, onOffsetChange, pageSizeOptions, onLimitChange, className } = props;

    // Hide when everything fits on one page — unless a page-size selector is
    // offered (keep it reachable) or the caller opts in via `alwaysShow`.
    const hasPageSizeSelect = !!(pageSizeOptions && pageSizeOptions.length > 0 && onLimitChange);
    if (total <= limit && !hasPageSizeSelect && !props.alwaysShow) {
      return null;
    }

    const totalPages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.floor(offset / limit) + 1;
    const canPrev = offset > 0;
    const canNext = offset + limit < total;
    const rangeStart = total === 0 ? 0 : offset + 1;
    const rangeEnd = Math.min(offset + limit, total);

    return (
      <nav aria-label="Pagination" className={cn("flex flex-wrap items-center justify-between gap-3 py-3", className)}>
        {/* Left: summary + rows-per-page */}
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <span aria-live="polite" aria-atomic="true">
            {rangeStart}–{rangeEnd} of {total.toLocaleString()}
          </span>

          {pageSizeOptions && pageSizeOptions.length > 0 && onLimitChange && (
            <div className="flex items-center gap-1.5">
              <label htmlFor="pagination-page-size" className="text-gray-500 dark:text-gray-400 select-none">
                Rows:
              </label>
              <select
                id="pagination-page-size"
                value={limit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  onLimitChange(newLimit);
                  onOffsetChange(0);
                }}
                className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {pageSizeOptions.map((sz) => (
                  <option key={sz} value={sz}>
                    {sz}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Right: page buttons + prev/next */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onOffsetChange(offset - limit)}
            disabled={!canPrev}
            aria-label="Previous page"
            className={NAV_BTN}
          >
            <ChevronLeft />
            <span className="ml-1 hidden sm:inline">Prev</span>
          </button>

          {showPageNumbers && totalPages > 1 && (
            <PageButtons current={currentPage} total={totalPages} onGo={(p) => onOffsetChange((p - 1) * limit)} />
          )}

          <button
            type="button"
            onClick={() => onOffsetChange(offset + limit)}
            disabled={!canNext}
            aria-label="Next page"
            className={NAV_BTN}
          >
            <span className="mr-1 hidden sm:inline">Next</span>
            <ChevronRight />
          </button>
        </div>
      </nav>
    );
  }

  // --- simple mode (default) ---
  const { page, totalPages, onPageChange, className } = props;

  // Hide when there is nothing to paginate, unless the caller opts in.
  if (totalPages <= 1 && !props.alwaysShow) {
    return null;
  }

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <nav aria-label="Pagination" className={cn("flex items-center gap-1.5", className)}>
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={!canPrev}
        aria-label="Previous page"
        className={NAV_BTN}
      >
        <ChevronLeft />
        <span className="ml-1 hidden sm:inline">Prev</span>
      </button>

      {showPageNumbers && totalPages > 1 && <PageButtons current={page} total={totalPages} onGo={onPageChange} />}

      {(!showPageNumbers || totalPages <= 1) && (
        <span className="text-sm text-gray-600 dark:text-gray-400 px-2" aria-live="polite" aria-atomic="true">
          Page {page} of {totalPages}
        </span>
      )}

      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={!canNext}
        aria-label="Next page"
        className={NAV_BTN}
      >
        <span className="mr-1 hidden sm:inline">Next</span>
        <ChevronRight />
      </button>
    </nav>
  );
}

Pagination.displayName = "Pagination";
